import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Download, Zap } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What should an electrician invoice include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'An electrician invoice should include your name or company name and electrical registration or licence number, the client name and job address, a unique invoice number and job date, a description of the electrical work completed, labour charges (hours and rate), materials and parts used, any call-out or emergency fee, applicable VAT or GST, certification notes if required, and your payment details.',
      },
    },
    {
      '@type': 'Question',
      name: 'Should electricians include their licence number on invoices?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes, in most countries this is a legal requirement or strong best practice. In the UK, NICEIC or NAPIT registration numbers should appear on invoices. In Australia, include your electrical contractor licence number. In the US, your state electrical contractor licence number should be listed. Including your registration builds trust and may be required for insurance claim purposes.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do electricians charge for their work?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Electricians typically charge a call-out fee plus an hourly labour rate, or a fixed price for well-defined jobs. Materials are charged at cost plus a markup (usually 15–30%). Emergency and out-of-hours work typically carries a premium rate. For larger installations, a detailed quote with staged payments is common. Always separate labour and materials on your invoice.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do electricians charge VAT on their invoices?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes, if VAT-registered. In the UK, electrical services are standard-rated at 20% VAT, though energy-saving installations may qualify for 5% reduced rate. In Australia, 10% GST applies if turnover exceeds AUD $75,000. In India, electrical services attract 18% GST. Always show VAT or GST as a separate line item on your invoice, not embedded in the price.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is this electrician invoice template free?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic provides a free electrician invoice template. Fill in your job details online and download a professional PDF instantly. Free for up to 5 invoices per month with no sign-up required.',
      },
    },
  ],
};

export default function ElectricianInvoiceTemplatePage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Electrician Invoice Template: Free PDF for Electricians | Invilic"
        description="Free electrician invoice template. Bill for labour, materials, call-out fees, and VAT/GST. Download a professional PDF instantly. No sign-up needed."
        canonical="https://www.invilic.com/electrician-invoice-template"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-16">
          <span className="inline-block bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            Free Template
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Electrician Invoice Template
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            A professional invoice template for electricians and electrical contractors. Bill for labour, materials, and call-out fees. Download a clean PDF in seconds — no account needed.
          </p>
          <Link
            to="/create/freelancer"
            className="inline-flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-yellow-500/20 transition-all duration-200 text-lg"
          >
            Use Free Template <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            Built for Every Type of Electrical Work
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { title: 'Domestic electricians', desc: 'Bill homeowners for rewires, consumer unit upgrades, fault-finding, and EV charger installations.' },
              { title: 'Commercial electricians', desc: 'Invoice businesses for commercial fit-outs, three-phase installations, and PAT testing.' },
              { title: 'Solar & renewable electricians', desc: 'Bill for solar panel installations, battery storage systems, and smart home wiring.' },
              { title: 'Industrial electricians', desc: 'Issue invoices for industrial machinery wiring, motor control, and factory maintenance contracts.' },
            ].map(({ title, desc }) => (
              <div key={title} className="flex gap-4 p-5 rounded-xl border border-gray-100 dark:border-gray-800">
                <div className="w-10 h-10 rounded-xl bg-yellow-100 dark:bg-yellow-900/40 flex items-center justify-center flex-shrink-0">
                  <Zap className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900 dark:text-white text-sm mb-1">{title}</div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            What's in the Electrician Invoice Template
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              'Business name and licence / registration number',
              'Client name and job address',
              'Invoice number and job date',
              'Payment due date',
              'Work description per line item',
              'Labour hours and hourly rate',
              'Call-out or emergency fee',
              'Materials, parts and components',
              'Certification or test fee (if applicable)',
              'Subtotal, VAT / GST and discount',
              'Total amount due',
              'Bank details or payment link',
            ].map(f => (
              <div key={f} className="flex items-center gap-3 p-4 rounded-xl border border-yellow-100 dark:border-yellow-800/30 bg-yellow-50 dark:bg-yellow-900/10">
                <CheckCircle className="w-4 h-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300">{f}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            How to Create Your Electrical Invoice
          </h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { n: '1', title: 'Open the template', desc: 'Click "Use Free Template" above. No software to install, no account needed.' },
              { n: '2', title: 'Fill in your job', desc: 'Enter your work, hours, materials, and any VAT or GST.' },
              { n: '3', title: 'Download PDF', desc: 'Download a professional PDF invoice ready to hand to your client or send by email.' },
            ].map(({ n, title, desc }) => (
              <div key={n} className="text-center p-6 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
                <div className="w-10 h-10 rounded-full bg-yellow-500 text-white font-bold text-lg flex items-center justify-center mx-auto mb-4">
                  {n}
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
            Electrician Invoice Template: FAQs
          </h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                  {acceptedAnswer.text}
                </p>
              </details>
            ))}
          </div>
        </section>

        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Create Your Electrical Invoice Now for Free
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Professional PDF. No account required. Ready in 2 minutes.</p>
          <Link
            to="/create/freelancer"
            className="inline-flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-yellow-500/20 transition-all duration-200 text-lg"
          >
            <Download className="w-5 h-5" /> Get Free Electrician Invoice Template
          </Link>
        </section>
      </main>
    </div>
  );
}
